// FacilitatorQuestionBankItemLibrary
// Unified Question library workspace. Top-tab toggle switches between:
//   - Questions: per-type item library (author standalone questions)
//   - Sets:      composition view of mcq_exercises rows (pick library
//                items into a named set; no inline authoring)
// All question authoring happens in the Questions tab. Sets are pure
// compositions over those items — editing a set means picking and
// removing items, not editing question content.

import React, { useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import {
  QBANK_ITEM_GROUPS,
  QBANK_ITEM_TYPE_LABELS,
  QBANK_ITEM_TYPE_ICONS,
} from '../../lib/questionBankItems';
import { DIFFICULTY_LABELS, DIFFICULTY_BADGE_BG, DIFFICULTY_BADGE_TEXT } from '../../lib/mcq';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const STATUS_BADGE_BG = { draft: 'secondary', published: 'success', archived: 'dark' };

const FacilitatorQuestionBankItemLibrary = ({
  // Top-tab state
  activeView = 'questions',
  onChangeView,
  // Items
  items = [],
  activeGroupKey = 'mcq',
  onChangeGroup,
  onCreate,
  onEdit,
  onPublish,
  onArchive,
  onDelete,
  // Sets
  sets = [],
  onCreateSet,
  onEditSet,
  onPublishSet,
  onArchiveSet,
  onDeleteSet,
  // Shared
  onBack,
}) => {
  return (
    <div className="student-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title="Question library"
          description={<>Author individual questions on the <strong>Questions</strong> tab. Pick them into named tests on the <strong>Sets</strong> tab.</>}
          onBack={onBack}
          backLabel="Back to home"
        />

        <Nav variant="tabs" className="mb-3 mt-3" activeKey={activeView}>
          <Nav.Item>
            <Nav.Link eventKey="questions" onClick={() => onChangeView?.('questions')}>
              <i className="bi bi-collection me-1" />Questions
              <Badge bg="light" text="dark" className="ms-2">{items.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="sets" onClick={() => onChangeView?.('sets')}>
              <i className="bi bi-stack me-1" />Sets
              <Badge bg="light" text="dark" className="ms-2">{sets.length}</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {activeView === 'sets' ? (
          <SetsView
            sets={sets}
            onCreate={onCreateSet}
            onEdit={onEditSet}
            onPublish={onPublishSet}
            onArchive={onArchiveSet}
            onDelete={onDeleteSet}
          />
        ) : (
          <QuestionsView
            items={items}
            activeGroupKey={activeGroupKey}
            onChangeGroup={onChangeGroup}
            onCreate={onCreate}
            onEdit={onEdit}
            onPublish={onPublish}
            onArchive={onArchive}
            onDelete={onDelete}
          />
        )}
      </Container>
    </div>
  );
};

// ============================================================================
// Questions tab — per-type item library (unchanged behaviour from before)
// ============================================================================

function QuestionsView({ items, activeGroupKey, onChangeGroup, onCreate, onEdit, onPublish, onArchive, onDelete }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [previewIndex, setPreviewIndex] = useState(null);
  const [previewShowAnswer, setPreviewShowAnswer] = useState(false);

  const activeGroup = QBANK_ITEM_GROUPS.find((g) => g.key === activeGroupKey) || QBANK_ITEM_GROUPS[0];

  const groupCounts = useMemo(() => {
    const counts = Object.fromEntries(QBANK_ITEM_GROUPS.map((g) => [g.key, 0]));
    for (const item of items) {
      const group = QBANK_ITEM_GROUPS.find((g) => g.types.includes(item.templateType));
      if (group) counts[group.key] += 1;
    }
    return counts;
  }, [items]);

  const filteredItems = useMemo(() => {
    const list = items.filter((item) => activeGroup.types.includes(item.templateType));
    const q = search.trim().toLowerCase();
    return list.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${item.promptExcerpt || ''} ${item.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, activeGroup, search, statusFilter]);

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <div className="d-flex gap-2 flex-wrap">
          <Button type="button" variant="outline-primary"
            onClick={() => { setPreviewIndex(0); setPreviewShowAnswer(false); }}
            disabled={filteredItems.length === 0}>
            <i className="bi bi-play-circle me-1" />Preview
          </Button>
          {activeGroup.types.length === 1 ? (
            <Button type="button" variant="primary" onClick={() => onCreate?.(activeGroup.types[0])}>
              <i className="bi bi-plus-lg me-1" />Add question
            </Button>
          ) : (
            <DropdownButton id="qbi-add" variant="primary"
              title={<><i className="bi bi-plus-lg me-1" />Add question</>}>
              {activeGroup.types.map((type) => (
                <Dropdown.Item key={type} onClick={() => onCreate?.(type)}>
                  <i className={`bi ${QBANK_ITEM_TYPE_ICONS[type] || 'bi-question-square'} me-2`} aria-hidden="true" />
                  {QBANK_ITEM_TYPE_LABELS[type]}
                </Dropdown.Item>
              ))}
            </DropdownButton>
          )}
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-3">
          <Card>
            <Card.Body className="p-2">
              <Nav variant="pills" className="flex-column" activeKey={activeGroup.key}>
                {QBANK_ITEM_GROUPS.map((group) => (
                  <Nav.Item key={group.key}>
                    <Nav.Link
                      eventKey={group.key}
                      onClick={() => onChangeGroup?.(group.key)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <i className={`bi ${group.icon} me-2`} aria-hidden="true" />
                        {group.label}
                      </span>
                      <Badge bg={group.key === activeGroup.key ? 'light' : 'secondary'}
                             text={group.key === activeGroup.key ? 'dark' : undefined}>
                        {groupCounts[group.key] || 0}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Card.Body>
          </Card>
        </div>

        <div className="col-12 col-lg-9">
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <InputGroup style={{ maxWidth: '24rem' }}>
                  <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control
                    placeholder="Search prompt or topic…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search ? (
                    <Button variant="outline-secondary" onClick={() => setSearch('')}>Clear</Button>
                  ) : null}
                </InputGroup>
                <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: '12rem' }}>
                  <option value="all">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </Form.Select>
                <div className="ms-auto small text-muted">
                  {filteredItems.length} of {groupCounts[activeGroup.key] || 0} shown
                </div>
              </div>
            </Card.Body>
          </Card>

          {filteredItems.length === 0 ? (
            <Alert variant="light" className="border text-center">
              <h5 className="mb-1">
                <i className={`bi ${activeGroup.icon} me-2`} aria-hidden="true" />
                No {activeGroup.label.toLowerCase()} questions yet
              </h5>
              <p className="text-muted mb-3">
                Use <strong>Add question</strong> above to author a standalone {activeGroup.label.toLowerCase()} item. Pick items into a set on the Sets tab.
              </p>
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {filteredItems.map((item) => (
                <Card key={item.id} className="qbi-row">
                  <Card.Body className="py-3">
                    <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <Badge bg="info">
                            <i className={`bi ${QBANK_ITEM_TYPE_ICONS[item.templateType] || 'bi-question-square'} me-1`} aria-hidden="true" />
                            {QBANK_ITEM_TYPE_LABELS[item.templateType] || item.templateType}
                          </Badge>
                          <Badge bg={STATUS_BADGE_BG[item.status] || 'secondary'}>
                            {item.status}
                          </Badge>
                          {item.category ? <Badge bg="light" text="dark"><i className="bi bi-tag me-1" />{item.category}</Badge> : null}
                          {item.difficulty ? (
                            <Badge bg={DIFFICULTY_BADGE_BG[item.difficulty] || 'secondary'}
                                   text={DIFFICULTY_BADGE_TEXT[item.difficulty]}>
                              {DIFFICULTY_LABELS[item.difficulty] || item.difficulty}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="fw-semibold">{item.promptExcerpt || <em className="text-muted">(no prompt yet)</em>}</div>
                        <div className="small text-muted mt-1">
                          {item.ownerDisplayName || item.ownerEmail || 'Unknown author'}
                          {item.updatedAt ? <> · updated {new Date(item.updatedAt).toLocaleDateString()}</> : null}
                        </div>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline-primary" onClick={() => onEdit?.(item)}>
                          <i className="bi bi-pencil me-1" />Edit
                        </Button>
                        {item.status !== 'published' ? (
                          <Button size="sm" variant="outline-success" onClick={() => onPublish?.(item.id)}>
                            Publish
                          </Button>
                        ) : null}
                        {item.status === 'published' ? (
                          <Button size="sm" variant="outline-dark" onClick={() => onArchive?.(item.id)}>
                            Archive
                          </Button>
                        ) : null}
                        <Button size="sm" variant="outline-danger" onClick={() => onDelete?.(item.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <PreviewModal
        items={filteredItems}
        index={previewIndex}
        showAnswer={previewShowAnswer}
        onClose={() => setPreviewIndex(null)}
        onPrev={() => { setPreviewIndex((i) => Math.max(0, (i ?? 0) - 1)); setPreviewShowAnswer(false); }}
        onNext={() => { setPreviewIndex((i) => Math.min(filteredItems.length - 1, (i ?? 0) + 1)); setPreviewShowAnswer(false); }}
        onToggleAnswer={() => setPreviewShowAnswer((v) => !v)}
      />
    </>
  );
}

// ============================================================================
// Sets tab — list of mcq_exercises rows with create/edit/publish/archive/delete
// ============================================================================

function SetsView({ sets, onCreate, onEdit, onPublish, onArchive, onDelete }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sets.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${s.title || ''} ${s.summary || ''} ${s.category || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sets, statusFilter, search]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))),
    [filtered]
  );

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <Button type="button" variant="primary" onClick={onCreate}>
          <i className="bi bi-plus-lg me-1" />Create set
        </Button>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <InputGroup style={{ maxWidth: '24rem' }}>
              <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
              <Form.Control
                placeholder="Search title, summary or topic…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search ? (
                <Button variant="outline-secondary" onClick={() => setSearch('')}>Clear</Button>
              ) : null}
            </InputGroup>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: '12rem' }}>
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Form.Select>
            <div className="ms-auto small text-muted">{sorted.length} of {sets.length} shown</div>
          </div>
        </Card.Body>
      </Card>

      {sorted.length === 0 ? (
        <Alert variant="light" className="border text-center">
          <h5 className="mb-1"><i className="bi bi-stack me-2" />No sets yet</h5>
          <p className="text-muted mb-3">
            Click <strong>Create set</strong> to assemble one from questions in your library.
          </p>
        </Alert>
      ) : (
        <div className="d-flex flex-column gap-2">
          {sorted.map((s) => {
            const questionCount = Array.isArray(s.payload?.templates) ? s.payload.templates.length : 0;
            return (
              <Card key={s.id} className="qbi-row">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <Badge bg={STATUS_BADGE_BG[s.status] || 'secondary'}>{s.status}</Badge>
                        <Badge bg="light" text="dark">{questionCount} question{questionCount === 1 ? '' : 's'}</Badge>
                        {s.category ? <Badge bg="light" text="dark"><i className="bi bi-tag me-1" />{s.category}</Badge> : null}
                        {s.difficulty ? (
                          <Badge bg={DIFFICULTY_BADGE_BG[s.difficulty] || 'secondary'}
                                 text={DIFFICULTY_BADGE_TEXT[s.difficulty]}>
                            {DIFFICULTY_LABELS[s.difficulty] || s.difficulty}
                          </Badge>
                        ) : null}
                        {s.isCuratedRevisionSet ? <Badge bg="warning" text="dark"><i className="bi bi-bookmark-star me-1" />Curated revision</Badge> : null}
                        {s.freeTierEligible ? <Badge bg="success">Free tier</Badge> : null}
                      </div>
                      <div className="fw-semibold">{s.title || <em className="text-muted">(no title)</em>}</div>
                      {s.summary ? <div className="small text-muted">{s.summary}</div> : null}
                      <div className="small text-muted mt-1">
                        {s.ownerDisplayName || s.ownerEmail || 'Unknown author'}
                        {s.updatedAt ? <> · updated {new Date(s.updatedAt).toLocaleDateString()}</> : null}
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline-primary" onClick={() => onEdit?.(s)}>
                        <i className="bi bi-pencil me-1" />Edit
                      </Button>
                      {s.status !== 'published' ? (
                        <Button size="sm" variant="outline-success" onClick={() => onPublish?.(s.id)}>
                          Publish
                        </Button>
                      ) : null}
                      {s.status === 'published' ? (
                        <Button size="sm" variant="outline-dark" onClick={() => onArchive?.(s.id)}>
                          Archive
                        </Button>
                      ) : null}
                      <Button size="sm" variant="outline-danger" onClick={() => onDelete?.(s.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

// Per-type renderer used inside the Preview modal. Shows the student's
// view of one item, with a click-to-reveal "Correct answer" panel and
// the teaching note. Read-only — no scoring or input. Authoring polish
// only, not a real session.
function PreviewModal({ items, index, showAnswer, onClose, onPrev, onNext, onToggleAnswer }) {
  const isOpen = index !== null && items[index];
  const item = isOpen ? items[index] : null;
  const t = item?.payload || {};
  const type = item?.templateType;
  const total = items.length;

  return (
    <Modal show={Boolean(isOpen)} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-play-circle me-2" />
          Preview
          {isOpen ? <span className="text-muted ms-2 small">Question {index + 1} of {total}</span> : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {item ? (
          <>
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <Badge bg="info">
                <i className={`bi ${QBANK_ITEM_TYPE_ICONS[type] || 'bi-question-square'} me-1`} aria-hidden="true" />
                {QBANK_ITEM_TYPE_LABELS[type] || type}
              </Badge>
              {item.category ? <Badge bg="light" text="dark"><i className="bi bi-tag me-1" />{item.category}</Badge> : null}
              {item.difficulty ? (
                <Badge bg={DIFFICULTY_BADGE_BG[item.difficulty] || 'secondary'}
                       text={DIFFICULTY_BADGE_TEXT[item.difficulty]}>
                  {DIFFICULTY_LABELS[item.difficulty] || item.difficulty}
                </Badge>
              ) : null}
            </div>

            <div className="mb-3 fs-5">{t.prompt || <em className="text-muted">(no prompt)</em>}</div>

            {type === 'mcq' || type === 'multi-mcq' ? (
              <div className="d-flex flex-column gap-2 mb-3">
                {(t.options || []).map((opt, i) => {
                  const isCorrect = type === 'multi-mcq'
                    ? Array.isArray(t.correctIndices) && t.correctIndices.includes(i)
                    : Number(t.correctIndex) === i;
                  return (
                    <div key={i}
                         className={`p-2 border rounded ${showAnswer && isCorrect ? 'border-success bg-success-subtle' : ''}`}>
                      <strong className="me-2">{String.fromCharCode(65 + i)}.</strong>{opt}
                      {showAnswer && isCorrect ? <i className="bi bi-check-circle-fill text-success ms-2" /> : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {type === 'true-false' ? (
              <div className="d-flex gap-2 mb-3">
                <div className={`p-3 border rounded flex-grow-1 text-center ${showAnswer && t.correctAnswer === true ? 'border-success bg-success-subtle' : ''}`}>
                  True {showAnswer && t.correctAnswer === true ? <i className="bi bi-check-circle-fill text-success ms-1" /> : null}
                </div>
                <div className={`p-3 border rounded flex-grow-1 text-center ${showAnswer && t.correctAnswer === false ? 'border-success bg-success-subtle' : ''}`}>
                  False {showAnswer && t.correctAnswer === false ? <i className="bi bi-check-circle-fill text-success ms-1" /> : null}
                </div>
              </div>
            ) : null}

            {type === 'cloze' ? (
              <div className="mb-3">
                {showAnswer ? (
                  <div className="d-flex flex-column gap-2">
                    {(t.blanks || []).map((b, i) => (
                      <div key={i} className="border rounded p-2 bg-success-subtle">
                        <strong>{`{{${i + 1}}}`}</strong>{' '}— accepted: {(b.answers || []).filter(Boolean).join(', ') || <em>(none)</em>}
                        {b.caseSensitive ? <span className="small text-muted ms-2">case-sensitive</span> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="small text-muted">Click "Show answer" to reveal accepted answers for each <code>{'{{n}}'}</code> blank.</div>
                )}
              </div>
            ) : null}

            {type === 'ordering' ? (
              <div className="d-flex flex-column gap-2 mb-3">
                {(t.items || []).map((it, i) => (
                  <div key={it.id || i} className={`border rounded p-2 ${showAnswer ? 'border-success bg-success-subtle' : ''}`}>
                    <strong className="me-2">{i + 1}.</strong>{it.text}
                  </div>
                ))}
                {!showAnswer ? <div className="small text-muted">Order shown is the correct order — students see it shuffled.</div> : null}
              </div>
            ) : null}

            {type === 'bucket-sort' ? (
              <div className="mb-3">
                <Row className="g-2">
                  {(t.buckets || []).map((b) => (
                    <Col key={b.id} md={6}>
                      <Card className={showAnswer ? 'border-success' : ''}>
                        <Card.Header className="py-2 fw-semibold">{b.label}</Card.Header>
                        <Card.Body className="py-2">
                          {(t.items || []).filter((it) => it.bucketId === b.id).map((it) => (
                            <div key={it.id} className={showAnswer ? 'text-success' : 'text-muted'}>
                              {showAnswer ? <i className="bi bi-check-circle-fill me-1" /> : <i className="bi bi-dot me-1" />}
                              {it.text}
                            </div>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {!showAnswer ? <div className="small text-muted mt-2">Items shown in their correct buckets — students see them in an unsorted tray.</div> : null}
              </div>
            ) : null}

            {type === 'matching' ? (
              <div className="d-flex flex-column gap-2 mb-3">
                {(t.pairs || []).map((p) => (
                  <Row key={p.id} className={`align-items-center g-2 border rounded p-2 ${showAnswer ? 'border-success bg-success-subtle' : ''}`}>
                    <Col>{p.left}</Col>
                    <Col xs="auto" className="text-muted">↔</Col>
                    <Col>{p.right}</Col>
                  </Row>
                ))}
                {!showAnswer ? <div className="small text-muted">Pairs shown aligned — students see the right column shuffled.</div> : null}
              </div>
            ) : null}

            {type === 'fixed' ? (
              <div className="mb-3">
                {showAnswer ? (
                  <div className="border rounded p-3 bg-success-subtle">
                    <strong>Expected answer:</strong> {t.expectedAnswer}
                    {t.unit ? <> {t.unit}</> : null}
                    {t.tolerance ? (
                      <div className="small text-muted mt-1">
                        Tolerance: {t.tolerance.type === 'exact' ? 'Exact match required'
                          : t.tolerance.type === 'percent' ? `±${t.tolerance.value}%`
                          : `±${t.tolerance.value}${t.unit ? ` ${t.unit}` : ''}`}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="border rounded p-3 bg-light">
                    <Form.Control disabled placeholder="Student types their numeric answer here…" />
                  </div>
                )}
              </div>
            ) : null}

            {type === 'calculation' ? (
              <div className="mb-3">
                {showAnswer ? (
                  <div className="border rounded p-3 bg-success-subtle">
                    <div><strong>Formula:</strong> <code>{t.formula}</code>{t.unit ? <> ({t.unit})</> : null}</div>
                    {Array.isArray(t.variables) && t.variables.length ? (
                      <div className="small text-muted mt-2">
                        Variables: {t.variables.map((v, i) => (
                          <span key={i} className="me-2">
                            <code>{v.name}</code> [{v.min}–{v.max}{v.step ? ` step ${v.step}` : ''}]{v.unit ? ` ${v.unit}` : ''}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {t.tolerance ? (
                      <div className="small text-muted mt-1">
                        Tolerance: {t.tolerance.type === 'percent' ? `±${t.tolerance.value}%` : `±${t.tolerance.value}${t.unit ? ` ${t.unit}` : ''}`}
                      </div>
                    ) : null}
                    <div className="small text-muted mt-2">
                      The engine samples fresh values for each variable per play and grades to this tolerance.
                    </div>
                  </div>
                ) : (
                  <div className="border rounded p-3 bg-light">
                    <Form.Control disabled placeholder="Student types their numeric answer here (after the engine substitutes sampled values)…" />
                  </div>
                )}
              </div>
            ) : null}

            <div className="d-flex justify-content-center my-3">
              <Button variant={showAnswer ? 'outline-secondary' : 'success'} onClick={onToggleAnswer}>
                {showAnswer ? 'Hide answer' : 'Show answer'}
              </Button>
            </div>

            {showAnswer && t.notes ? (
              <Alert variant="info" className="mb-0">
                <strong><i className="bi bi-lightbulb me-1" />Teaching note:</strong> {t.notes}
              </Alert>
            ) : null}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onPrev} disabled={index === 0}>
          <i className="bi bi-chevron-left me-1" />Previous
        </Button>
        <Button variant="outline-secondary" onClick={onClose}>Close</Button>
        <Button variant="outline-primary" onClick={onNext} disabled={index >= total - 1}>
          Next<i className="bi bi-chevron-right ms-1" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default FacilitatorQuestionBankItemLibrary;
